import { useLocation, useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Heart } from "lucide-react";
import { cn } from "@/lib/utils";

interface AuthPromptDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
  /** Where "Create an account" navigates (default: the standard signup). */
  signupTo?: string;
  /** Where "Log in" navigates. */
  loginTo?: string;
  /** Override the primary CTA label. */
  signupLabel?: string;
  /** z-index overrides so the dialog can sit above the concierge overlay. */
  overlayClassName?: string;
  contentClassName?: string;
}

const AuthPromptDialog = ({
  open,
  onOpenChange,
  title = "Save your favorites",
  description = "Create a free account or log in to save properties you love and access them anytime.",
  signupTo,
  loginTo,
  signupLabel = "Create an account",
  overlayClassName,
  contentClassName,
}: AuthPromptDialogProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  // Default targets carry the current page as ?redirect= so the user comes
  // back here after auth instead of being dropped on a generic page.
  const returnTo = encodeURIComponent(location.pathname + location.search);
  const signupTarget = signupTo ?? `/login?mode=signup&redirect=${returnTo}`;
  const loginTarget = loginTo ?? `/login?mode=signin&redirect=${returnTo}`;

  const handleNavigate = (path: string) => {
    onOpenChange(false);
    navigate(path);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn("sm:max-w-md", contentClassName)} overlayClassName={overlayClassName}>
        <DialogHeader>
          <div className="mx-auto mb-3 h-12 w-12 rounded-full bg-secondary flex items-center justify-center">
            <Heart className="h-6 w-6 text-primary" />
          </div>
          <DialogTitle className="text-center font-serif text-xl">{title}</DialogTitle>
          <DialogDescription className="text-center">{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-col sm:flex-col gap-2 sm:gap-2 mt-2">
          <Button className="w-full" onClick={() => handleNavigate(signupTarget)}>
            {signupLabel}
          </Button>
          <Button variant="outline" className="w-full" onClick={() => handleNavigate(loginTarget)}>
            Log in
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AuthPromptDialog;
